import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { inputCls, ghostBtnCls, primaryBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const STATUS_MAP = { all: undefined, new: "new", in_progress: "in_progress", final: "result_entered", verified: "verified", signed_off: "signed_off", released: "released" };
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

// mode: "today" | "search"
export default function LabWorklist({ mode = "today" }) {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");
  const [day, setDay] = useState(today());
  const [f, setF] = useState({ from_date: "", to_date: "", q: "", reg_no: "", status: "" });

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = {};
      if (mode === "today") { params.from_date = day; params.to_date = day; if (STATUS_MAP[tab]) params.status = STATUS_MAP[tab]; }
      else { Object.assign(params, { from_date: f.from_date || undefined, to_date: f.to_date || undefined, q: f.q || undefined, reg_no: f.reg_no || undefined, status: f.status || undefined }); }
      const { data } = await api.get("/lab/bills", { params });
      setItems(data.items);
      if (mode === "today") { const { data: s } = await api.get("/lab/worklist/stats", { params: { day } }); setStats(s); }
    } catch (e) { setError(apiError(e)); }
  }, [mode, tab, day, f]);
  useEffect(() => { if (mode === "today") load(); /* eslint-disable-next-line */ }, [mode, tab, day]);
  useEffect(() => { if (mode === "search") load(); /* eslint-disable-next-line */ }, [mode]);

  if (!can("lab.orders.view")) return <NoAccess what="lab reports" />;

  const Row = (b) => (
    <tr key={b.bill_id} data-testid={`worklist-row-${b.bill_id}`}>
      <Td className="font-mono text-slate-700">{b.reg_no}</Td>
      <Td className="text-slate-500 text-xs">{(b.created_at || "").slice(0, 16).replace("T", " ")}</Td>
      <Td className="font-medium text-slate-900">{b.patient_title} {b.patient_name}</Td>
      <Td className="text-slate-600">{b.referrer_name || "—"}</Td>
      <Td>{b.report_released ? <span className="text-xs text-emerald-600 font-medium">Printed</span> : <span className="text-xs text-slate-400">—</span>}</Td>
      <Td><StatusPill status={b.status} /></Td>
      <Td><Link to={`/app/lab/bills/${b.bill_id}`} className="underline text-sm" data-testid={`worklist-view-${b.bill_id}`}>Open</Link></Td>
    </tr>
  );
  const cols = ["Reg", "Date/time", "Patient", "Referred by", "Printed", "Status", ""];

  return (
    <div data-testid={`lab-worklist-${mode}`}>
      <PageHeader overline="Laboratory" title={mode === "today" ? "Today's Reports" : "Search Reports"}
        subtitle={mode === "today" ? "Live worklist of today's cases by workflow status." : "Search released and in-progress reports across any date range."} />

      {mode === "today" ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <button className={ghostBtnCls} onClick={() => setDay(new Date(new Date(day).getTime() - 86400000).toISOString().slice(0, 10))} data-testid="day-prev">‹ Prev</button>
            <input type="date" className={inputCls} value={day} onChange={(e) => setDay(e.target.value)} data-testid="day-picker" />
            <button className={ghostBtnCls} onClick={() => setDay(new Date(new Date(day).getTime() + 86400000).toISOString().slice(0, 10))} data-testid="day-next">Next ›</button>
            <button className={ghostBtnCls} onClick={() => setDay(today())}>Today</button>
          </div>
          {stats && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="worklist-tabs">
              {["all", "new", "in_progress", "final", "signed_off", "released"].map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                  className={`px-4 py-2 rounded-full text-sm capitalize ${tab === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {t.replace("_", " ")} <span className="opacity-70">({stats[t] ?? 0})</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="panel p-4 mb-5 grid grid-cols-2 md:grid-cols-5 gap-3">
          <input type="date" className={inputCls} value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="search-from" />
          <input type="date" className={inputCls} value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="search-to" />
          <input className={inputCls} placeholder="Patient / UHID" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="search-q" />
          <select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="search-status"><option value="">Any status</option>{["new", "in_progress", "result_entered", "verified", "signed_off", "released"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select>
          <div className="flex gap-2"><button className={primaryBtnCls} data-testid="search-btn">Search</button><button type="button" className={ghostBtnCls} onClick={() => { setF({ from_date: "", to_date: "", q: "", reg_no: "", status: "" }); }}>Clear</button></div>
        </form>
      )}

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="worklist-table" columns={cols} rows={items} renderRow={Row}
          empty={<EmptyState title="No reports" subtitle="Nothing matches the current view." testid="worklist-empty" />} />
      )}
    </div>
  );
}
